import React, { useEffect, useState } from "react";
import { Check, Link2 } from "lucide-react";
import {
  BottomSheet,
  Button,
  Modal,
  useIsMobile,
  useToast,
} from "../../../components/ui";
import type { PreconsultationFormLink } from "../../preconsultation/services/preconsultationFormsService";

/** How long the copied tick stands before the button is a link icon again. */
const COPIED_MS = 2000;

export interface PreconsultationSharedModalProps {
  /** Their name, so the dialog says whose form has gone out. */
  clientName: string;
  /** Their number, for the WhatsApp draft — ten digits, no dialling code. */
  phone: string;
  /** The link the server has already raised. This dialog only hands it over. */
  link: PreconsultationFormLink;
  /**
   * Whether the link was raised a moment ago by the button that opened this,
   * or is one that went out some time before and is being handed over again.
   *
   * Only the words change. A freshly raised link comes with the server's own
   * email behind it, and saying so is the point of the dialog; a link being
   * resent has no fresh email behind it, and a dialog claiming one would be
   * telling a practitioner that something happened which did not.
   */
  fresh?: boolean;
  onClose: () => void;
}

/**
 * The dialog that hands a pre-consultation link over — the moment one is
 * raised, and again whenever the practitioner asks to send it out afresh.
 *
 * It reports rather than asks. The link is raised by the button that was
 * pressed — sending a client a form is not a thing that needs confirming, and
 * a dialog that opened to ask "shall I?" put a step between a plain intention
 * and the only thing it could have meant. By the time this is on screen the
 * server has already mailed the client; everything here is the second way of
 * getting it to them, because a practitioner knows which of the two numbers on
 * the record their client actually reads, and a message from a person is
 * opened where one from a system is not.
 *
 * A resend opens the same dialog over the link that is already out, rather
 * than minting a second one: the client may be halfway through the form they
 * hold, and a fresh link would kill it under them. See `fresh`, which is only
 * a matter of words.
 *
 * The address is never assembled here. What the server handed back is what is
 * copied — see `createPreconsultationForm`.
 */
const PreconsultationSharedModal: React.FC<PreconsultationSharedModalProps> = ({
  clientName,
  phone,
  link,
  fresh = true,
  onClose,
}) => {
  const toast = useToast();
  const [copied, setCopied] = useState(false);

  // Which shell the link is handed over in — a centred dialog, or a sheet up
  // from the bottom edge. See the branch at the end.
  const isMobile = useIsMobile();

  // The dialog's one name, read by `Modal` for the accessibility tree and
  // drawn again under the tick — see the heading below, which is why it is a
  // value rather than two literals that could drift apart.
  const heading = fresh
    ? "Pre-consultation form shared"
    : "Share the pre-consultation link";

  // The tick is a moment, not a state to be left in.
  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), COPIED_MS);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
    } catch {
      // A browser that refuses the clipboard — an insecure origin, a denied
      // permission — still leaves the address selectable in the box beside it.
      toast.warning(
        "Could not copy the link",
        "Select the address and copy it yourself."
      );
    }
  };

  /**
   * WhatsApp, with the message already written and the client's own number in
   * it. `wa.me` rather than the desktop app's scheme, so it opens whichever of
   * the two the practitioner actually uses.
   */
  const handleWhatsApp = () => {
    const text = encodeURIComponent(
      `Hi ${clientName}, please fill in this short form before our first ` +
        `consultation. It takes about five minutes: ${link.url}`
    );
    const digits = phone.replace(/\D/g, "");
    const to = digits.length === 10 ? `91${digits}` : digits;

    window.open(
      to ? `https://wa.me/${to}?text=${text}` : `https://wa.me/?text=${text}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  /**
   * Sending it on WhatsApp and being done are not a pair of answers, so they
   * sit at opposite ends rather than stacked to the right: WhatsApp as plain
   * text, and the way out carrying the button.
   *
   * One row whichever shell is drawn. The sheet stretches whatever it is
   * handed across the phone's width, and what it is handed here is this row —
   * so the two keep the ends they have on a desktop rather than becoming a
   * pair of half-width buttons, which would make a written action look like an
   * answer to a question nobody asked.
   */
  const actions = (
    <div className="flex w-full flex-wrap items-center justify-between gap-3">
      {/* Important on all three, because each is the same utility the
          variant or size already sets and CSS order would otherwise decide
          it. The padding goes so the label starts on the panel's own left
          edge, in line with the text above it rather than inset from it;
          the deep teal is the sidebar's ink, which reads as a written
          action rather than as a second button; and the hover wash goes
          with the padding, since a tint with no box around it only
          smudges. */}
      <Button
        variant="quiet"
        size="md"
        className="!px-0 !text-[#00424B] hover:!bg-transparent hover:underline"
        onClick={handleWhatsApp}
      >
        Send on WhatsApp
      </Button>
      <Button variant="outline" size="md" onClick={onClose}>
        Okay, got it
      </Button>
    </div>
  );

  const body = (
    <div>
      {/* Green, and a ring rather than a fill: it says the errand is done,
          which is the same thing the Accept on the strip says, and a filled
          disc at this size reads as a badge on the dialog rather than as its
          first word. Only on a link just raised — over a resend it would be
          ticking off something the practitioner has not done yet. */}
      {fresh && (
        <span
          aria-hidden="true"
          className="flex h-11 w-11 items-center justify-center rounded-full border-2 border-[#16A34A] text-[#16A34A]"
        >
          <Check size={22} strokeWidth={2.5} />
        </span>
      )}

      {/* Hidden from the accessibility tree, and only from that: the shell
          already carries these same words as the dialog's own name, and a
          screen reader that met both would say them twice. */}
      <h2
        aria-hidden="true"
        className={`${
          fresh ? "mt-5" : ""
        } text-[19px] 3xl:text-[21px] font-medium text-ink`}
      >
        {heading}
      </h2>

      <p className="mt-3 text-[14px] 3xl:text-[15px] leading-relaxed text-ink-muted">
        {fresh ? (
          <>
            The form link has been shared with{" "}
            <span className="font-medium text-ink">{clientName}</span> via
            email. You can also copy the link below and share it on WhatsApp or
            any other platform.
          </>
        ) : (
          <>
            This is the link already open for{" "}
            <span className="font-medium text-ink">{clientName}</span>. Send it
            on WhatsApp or copy it and share it anywhere — it is the same link,
            so anything they have already filled in is still there.
          </>
        )}
      </p>

      {/* The address on its own line, full width: it is the thing the dialog
          exists to hand over, and sharing the row with a labelled button
          truncated it to about half of what a practitioner could read. The
          copy sits outside the box as an icon for the same reason. */}
      <div className="mt-5 flex items-center gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-2.5 rounded-[8px] border border-[#E0E0E0] bg-white px-3.5 py-3">
          <Link2
            size={16}
            aria-hidden="true"
            className="flex-shrink-0 text-ink-muted"
          />
          {/* Selectable rather than an input: nothing here is editable, and
              a read-only box that looks like a field invites somebody to try
              to correct an address they must not change. */}
          <span className="truncate text-[14px] text-ink" title={link.url}>
            {link.url}
          </span>
        </div>

        {/* Not a `Button`: none of the variants is a square of the field's
            own height, and the label it would carry is the icon. */}
        <button
          type="button"
          onClick={handleCopy}
          aria-label={copied ? "Link copied" : "Copy link"}
          className="flex h-[46px] w-[46px] flex-shrink-0 items-center justify-center rounded-[8px] bg-[#00424B] text-white transition-colors hover:bg-[#00363D] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2CB]/40"
        >
          {copied ? <Check size={18} /> : <Link2 size={18} />}
        </button>
      </div>
    </div>
  );

  // A phone gets the sheet, as every other dialog in the app does: the link
  // arrives at the edge the thumb is already at, with the copy button and the
  // way out across it, rather than in a panel floating in the middle of the
  // screen. Which shell is a decision a stylesheet cannot make — see
  // `useIsMobile`.
  //
  // The heading is hidden on this shell too, for the reason it is hidden on
  // the other: it belongs under the tick, with the thing that has just
  // happened, rather than in a bar above it. `title` still names the sheet to
  // a screen reader.
  if (isMobile) {
    return (
      <BottomSheet
        open
        title={heading}
        hideTitle
        onClose={onClose}
        footer={actions}
      >
        <div className="px-5 pb-2 pt-1">{body}</div>
      </BottomSheet>
    );
  }

  return (
    <Modal
      open
      title={heading}
      // The heading is drawn in the body under the tick, where it belongs to
      // the thing that has just happened rather than sitting in a bar above it.
      hideTitle
      onClose={onClose}
      footer={actions}
    >
      {body}
    </Modal>
  );
};

export default PreconsultationSharedModal;
